/**
 * /execution/installers — Consolidated Installers surface.
 * ─────────────────────────────────────────────────────────
 * One door, two lenses. Tabs:
 *   1. Availability (default) — operational "can I dispatch this week?"
 *   2. Health — strategic "should I dispatch?" (vetting / score / callbacks / holdbacks)
 *
 * Each lens stays separate logically. The two existing surfaces
 * (AvailabilityFleetPage + ExecutionInstallerNetworkPage) are preserved
 * conceptually and inlined here so we can drive a shared header (search
 * + vetting filter) without nesting CockpitPage shells.
 *
 * Cross-references:
 *  - Availability rows show a small Health dot (score band) beside the name.
 *  - Health rows show a small Availability badge column.
 *
 * Hard rules preserved:
 *  - RBAC via canAccessExecutionDomain.
 *  - No new resolvers / writers. Reuses getContractorAvailability +
 *    computeExecutionScore exactly as the prior pages did.
 *  - No price/pay/PII rendered.
 *  - Contractor deep-links continue to point at /execution/vetting/:id.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CalendarRange, Search, List as ListIcon, LayoutGrid,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AvailabilityEditor } from "@/components/contractor/AvailabilityEditor";
import { useAppData } from "@/data/AppDataContext";
import { useReactQueryContractors } from "@/hooks/useReactQueryContractors";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { ContractorService } from "@/services/contractor.service";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import {
  getContractorAvailability,
  describeAvailability,
  type AvailabilityState,
} from "@/data/contractor/availability";
import { computeExecutionScore } from "@/data/contractor/networkExecution/executionScore";
import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";
import type { Contractor } from "@/data/types";

type ViewMode = "list" | "pods";
type TabId = "availability" | "health";
type VettingFilter = "all" | "approved" | "probation" | "pending";

const DAY = 86_400_000;

const VIEW_OPTIONS: Array<{ id: ViewMode; label: string; icon: typeof ListIcon }> = [
  { id: "list", label: "List", icon: ListIcon },
  { id: "pods", label: "Pods", icon: LayoutGrid },
];

const VETTING_OPTIONS: Array<{ id: VettingFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "approved", label: "Approved" },
  { id: "probation", label: "Probation" },
  { id: "pending", label: "Pending" },
];

function availabilityTone(state: AvailabilityState) {
  switch (state.mode) {
    case "available":
      return "text-success border-success/30 bg-success/5";
    case "limited":
    case "at_capacity":
      return "text-warning border-warning/30 bg-warning/5";
    default:
      return "text-muted-foreground border-muted-foreground/20 bg-muted/30";
  }
}

/** Small colored dot conveying score band; tooltip via title attr. */
function HealthDot({ score, probation }: { score: number | null; probation: boolean }) {
  let cls = "bg-muted-foreground/40";
  let label = "Score —";
  if (probation) {
    cls = "bg-warning";
    label = "On probation";
  } else if (score === null) {
    label = `Score — (sample < ${MIN_SAMPLE_FOR_DISPLAY})`;
  } else if (score >= 85) {
    cls = "bg-success";
    label = `Health ${score}`;
  } else if (score >= 70) {
    cls = "bg-warning/80";
    label = `Health ${score}`;
  } else {
    cls = "bg-destructive";
    label = `Health ${score}`;
  }
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${cls}`}
    />
  );
}

function passesVettingFilter(c: Contractor, f: VettingFilter): boolean {
  if (f === "all") return true;
  if (f === "probation") return Boolean(c.isOnProbation);
  if (f === "approved") return c.vettingStatus === "approved" && !c.isOnProbation;
  if (f === "pending") return c.vettingStatus !== "approved";
  return true;
}

function matchesQuery(c: Contractor, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    (c.companyName ?? "").toLowerCase().includes(needle) ||
    (c.contactName ?? "").toLowerCase().includes(needle)
  );
}

export default function InstallersPage() {
  const { role } = useRole();
  const { callbacks, holdbackLedger } = useAppData();
  const { contractors } = useReactQueryContractors();
  const { jobs } = useJobsQuery({ view: "all", search: "" });
  const [params, setParams] = useSearchParams();

  const initialTab: TabId =
    params.get("tab") === "health" ? "health" : "availability";
  const [tab, setTab] = useState<TabId>(initialTab);
  const [query, setQuery] = useState("");
  const [vetting, setVetting] = useState<VettingFilter>("all");
  const [view, setView] = useState<ViewMode>("list");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const handleSaveAvailability = async (
    contractorId: string,
    state: AvailabilityState,
  ) => {
    await ContractorService.updateAvailability(contractorId, state, {
      id: contractorId,
      role: role || "operator",
    });
  };

  /** Health enrichment per contractor — used by BOTH tabs (dot + table). */
  const healthByContractor = useMemo(() => {
    const cutoff = Date.now() - 30 * DAY;
    const m = new Map<string, { cb30: number; holdbacks: number; score: number | null }>();
    for (const c of contractors) {
      const cb30 = callbacks.filter(
        (k) =>
          k.installerContractorIdAtCallback === c.id &&
          new Date(k.filedAt).getTime() >= cutoff,
      ).length;
      const activeHbIds = new Set<string>();
      const releasedHbIds = new Set<string>();
      for (const e of holdbackLedger) {
        if (e.contractorId !== c.id) continue;
        if (e.action === "release" || e.action === "reverse") {
          releasedHbIds.add(e.holdbackId);
        } else {
          activeHbIds.add(e.holdbackId);
        }
      }
      releasedHbIds.forEach((id) => activeHbIds.delete(id));
      const score = computeExecutionScore({ contractor: c, jobs, callbacks }).score;
      m.set(c.id, { cb30, holdbacks: activeHbIds.size, score });
    }
    return m;
  }, [contractors, jobs, callbacks, holdbackLedger]);

  const availabilityByContractor = useMemo(() => {
    const m = new Map<string, AvailabilityState>();
    for (const c of contractors) {
      m.set(c.id, getContractorAvailability(c, jobs).state);
    }
    return m;
  }, [contractors, jobs]);

  /** Availability tab rows — full fleet (executor + sales + both). */
  const availabilityRows = useMemo(() => {
    const weight = (mode: string) =>
      mode === "available" ? 0 :
      mode === "limited" ? 1 :
      mode === "at_capacity" ? 2 :
      mode === "pto" ? 3 : 4;
    return contractors
      .filter((c) => matchesQuery(c, query) && passesVettingFilter(c, vetting))
      .map((c) => ({
        contractor: c,
        state: availabilityByContractor.get(c.id) ?? { mode: "available" as const },
        health: healthByContractor.get(c.id) ?? { cb30: 0, holdbacks: 0, score: null },
      }))
      .sort((a, b) => {
        const dw = weight(a.state.mode) - weight(b.state.mode);
        if (dw !== 0) return dw;
        return (a.contractor.companyName ?? "").localeCompare(
          b.contractor.companyName ?? "",
        );
      });
  }, [contractors, query, vetting, availabilityByContractor, healthByContractor]);

  /** Health tab rows — executor/both only (legacy filter preserved). */
  const healthRows = useMemo(() => {
    return contractors
      .filter((c) => {
        const br = c.businessRole;
        const isExecutor = br === "executor" || br === "both" || br === undefined;
        return isExecutor && matchesQuery(c, query) && passesVettingFilter(c, vetting);
      })
      .map((c) => ({
        c,
        ...(healthByContractor.get(c.id) ?? { cb30: 0, holdbacks: 0, score: null }),
        availability: availabilityByContractor.get(c.id) ?? { mode: "available" as const },
      }))
      .sort((a, b) => {
        if (a.c.isOnProbation !== b.c.isOnProbation) return a.c.isOnProbation ? -1 : 1;
        const sa = a.score ?? 999;
        const sb = b.score ?? 999;
        return sa - sb;
      });
  }, [contractors, query, vetting, healthByContractor, availabilityByContractor]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onTabChange = (next: string) => {
    const t = next === "health" ? "health" : "availability";
    setTab(t);
    const p = new URLSearchParams(params);
    p.set("tab", t);
    setParams(p, { replace: true });
  };

  if (!canAccessExecutionDomain(role)) return null;

  return (
    <CockpitPage title="Installers" maxWidth="7xl">
      <Tabs value={tab} onValueChange={onTabChange} className="space-y-4">
        {/* Shared header: tabs + search + vetting chip + (avail-only) view toggle */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList>
            <TabsTrigger value="availability">Availability</TabsTrigger>
            <TabsTrigger value="health">Health</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center rounded-md border border-border bg-background p-0.5">
              {VETTING_OPTIONS.map((opt) => {
                const active = vetting === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setVetting(opt.id)}
                    className={`px-2.5 py-1 rounded text-[11px] transition-colors ${
                      active
                        ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {tab === "availability" && (
              <div className="inline-flex items-center rounded-md border border-border bg-background p-0.5">
                {VIEW_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = view === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setView(opt.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] transition-colors ${
                        active
                          ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                      aria-pressed={active}
                    >
                      <Icon className="h-3 w-3" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search installer…"
                className="h-8 text-xs pl-8 w-56"
              />
            </div>
          </div>
        </div>

        {/* ─────── Availability tab ─────── */}
        <TabsContent value="availability" className="space-y-3 mt-0">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <CalendarRange className="h-4 w-4" />
            <span>
              Fleet-level installer availability. Declared modes win over
              derived load — At Capacity is a system-inferred read-only state.
            </span>
          </div>

          {availabilityRows.length === 0 ? (
            <Card className="p-8 text-center text-xs text-muted-foreground">
              No installers match this filter.
            </Card>
          ) : view === "pods" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {availabilityRows.map(({ contractor, health }) => (
                <div key={contractor.id} className="space-y-1">
                  <div className="flex items-baseline justify-between px-1">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      <HealthDot
                        score={health.score}
                        probation={Boolean(contractor.isOnProbation)}
                      />
                      {contractor.companyName || contractor.contactName || contractor.id}
                    </div>
                    {contractor.contactName &&
                      contractor.contactName !== contractor.companyName && (
                        <div className="text-[10px] text-muted-foreground">
                          {contractor.contactName}
                        </div>
                      )}
                  </div>
                  <AvailabilityEditor
                    contractor={contractor}
                    onAfterSave={(s) => handleSaveAvailability(contractor.id, s)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <Card className="overflow-hidden">
              <ul className="divide-y divide-border">
                {availabilityRows.map(({ contractor, state, health }) => {
                  const isOpen = expanded.has(contractor.id);
                  return (
                    <li key={contractor.id}>
                      <button
                        type="button"
                        onClick={() => toggleExpand(contractor.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
                        aria-expanded={isOpen}
                      >
                        {isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        <HealthDot
                          score={health.score}
                          probation={Boolean(contractor.isOnProbation)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {contractor.companyName || contractor.contactName || contractor.id}
                          </div>
                          {contractor.contactName &&
                            contractor.contactName !== contractor.companyName && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {contractor.contactName}
                              </div>
                            )}
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-[10px] shrink-0 ${availabilityTone(state)}`}
                        >
                          {describeAvailability(state)}
                        </Badge>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-1 bg-muted/20">
                          <AvailabilityEditor
                            contractor={contractor}
                            onAfterSave={(s) => handleSaveAvailability(contractor.id, s)}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </TabsContent>

        {/* ─────── Health tab ─────── */}
        <TabsContent value="health" className="space-y-3 mt-0">
          {healthRows.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No installers match this filter.
            </Card>
          ) : (
            <Card className="p-0 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contractor</TableHead>
                    <TableHead>Availability</TableHead>
                    <TableHead>Vetting</TableHead>
                    <TableHead>Probation</TableHead>
                    <TableHead className="text-right">Jobs done</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right">Callbacks 30d</TableHead>
                    <TableHead className="text-right">Active holdbacks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {healthRows.map(({ c, cb30, holdbacks, score, availability }) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Link
                          to={`/execution/vetting/${c.id}`}
                          className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:underline"
                        >
                          <HealthDot score={score} probation={Boolean(c.isOnProbation)} />
                          {c.companyName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${availabilityTone(availability)}`}
                        >
                          {describeAvailability(availability)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {c.vettingStatus ?? "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {c.isOnProbation ? (
                          <Badge className="bg-warning/15 text-warning border-warning/30 text-[10px]">
                            On probation
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-[11px] tabular-nums">
                        {c.jobsCompleted ?? 0}
                      </TableCell>
                      <TableCell className="text-right text-[11px] tabular-nums">
                        {score === null ? (
                          <span className="text-muted-foreground">
                            — (sample &lt; {MIN_SAMPLE_FOR_DISPLAY})
                          </span>
                        ) : (
                          score
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right text-[11px] tabular-nums ${
                          cb30 > 0 ? "text-warning font-semibold" : ""
                        }`}
                      >
                        {cb30}
                      </TableCell>
                      <TableCell
                        className={`text-right text-[11px] tabular-nums ${
                          holdbacks > 0 ? "text-warning font-semibold" : ""
                        }`}
                      >
                        {holdbacks}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </CockpitPage>
  );
}
